<template>
    <MyModal :zIndex="100" :isOpen="isOpen" :toggleModal="(v: boolean) => setIsOpen(v)">
        <div class="h-full font-inter" v-if="currentPage == 1">
            <div class="flex flex-col h-full">
                <!-- Header -->
                <div class="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                    <button class="transition duration-200 hover:text-primary p-2" @click="setIsOpen(false)">
                        <svg viewBox="0 0 24 24" role="presentation" style="width: 1.5rem; height: 1.5rem;"><path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" fill="currentColor"></path></svg>
                    </button>
                    <h2 class="font-bold text-lg">Carrinho de Compras</h2>
                    <div class="w-10"></div> <!-- Spacer -->
                </div>

                <!-- Items List -->
                <div class="flex-1 overflow-y-auto p-4 flex flex-col gap-4 pb-40">
                    <div v-if="cart.length === 0" class="flex flex-col items-center justify-center py-20 opacity-30">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.56-6.43h-13.78"/></svg>
                        <p class="mt-4 font-bold">Carrinho vazio</p>
                    </div>

                    <div v-for="(product, index) in cart" :key="product.cartId" class="bg-white border rounded-2xl overflow-hidden box-shadow transition hover:border-primary">
                        <div class="p-4 flex gap-4">
                            <img 
                                class="h-20 w-20 rounded-xl object-cover border" 
                                :src="product.media?.length ? (apiBaseUrl + '/storage/' + product.media[0].path) : '/assets/no-image.png'"
                                @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
                            >
                            <div class="flex-1">
                                <div class="flex justify-between items-start">
                                    <h5 class="font-bold text-gray-800 leading-tight">{{ product.name }}</h5>
                                    <button @click="cart.splice(index, 1)" class="text-gray-400 hover:text-red-500 transition">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                                    </button>
                                </div>
                                <p class="text-success font-bold text-sm mt-1">{{ product.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</p>
                                
                                <div class="flex items-center justify-between mt-3">
                                    <div class="flex gap-4 items-center bg-gray-100 p-1 px-3 rounded-full border">
                                        <button @click="product.quantity > 1 ? product.quantity-- : cart.splice(index, 1)" class="text-gray-500 hover:text-primary transition">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                                        </button>
                                        <span class="font-bold text-xs">{{ product.quantity }}</span>
                                        <button @click="product.quantity++" class="text-gray-500 hover:text-primary transition">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                                        </button>
                                    </div>
                                    <button 
                                        v-if="hasExtras(product)"
                                        @click="product.seeMore = !product.seeMore" 
                                        class="text-[10px] font-bold uppercase text-gray-400 hover:text-primary flex items-center gap-1"
                                    >
                                        {{ product.seeMore ? 'Ocultar' : 'Detalhes' }}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" :class="product.seeMore ? 'rotate-180' : ''" class="transition"><path d="m6 9 6 6 6-6"/></svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Details expanded -->
                        <div v-if="product.seeMore" class="px-4 pb-4 bg-gray-50/50 border-t">
                             <!-- Variants -->
                             <div class="mt-3 flex flex-col gap-2">
                                <div v-for="(variantIds, groupId) in product.selectedVariants" :key="groupId">
                                    <div v-if="variantIds.length > 0">
                                        <p class="text-[9px] uppercase font-bold text-gray-400 mb-1 border-b pb-1">
                                            {{ getVariantGroupName(product, parseInt(groupId as string)) }}
                                        </p>
                                        <div class="flex flex-wrap gap-2">
                                            <span v-for="vId in variantIds" :key="vId" class="text-[11px] bg-white border px-2 py-0.5 rounded-lg text-gray-600 font-medium">
                                                {{ getVariantName(product, parseInt(groupId as string), vId) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                             </div>

                             <!-- AddOns -->
                             <div class="mt-3 flex flex-col gap-2">
                                <div v-if="Object.keys(product.selectedAddOns).length > 0">
                                     <p class="text-[9px] uppercase font-bold text-gray-400 mb-1 border-b pb-1">Adicionais</p>
                                     <div class="flex flex-col gap-1">
                                         <div v-for="(qty, addonId) in product.selectedAddOns" :key="addonId" class="flex justify-between items-center text-[11px]">
                                             <span class="text-gray-600 font-medium">{{ qty }}x {{ getAddOnName(product, parseInt(addonId as string)) }}</span>
                                             <span class="text-gray-400 font-bold">+{{ (getAddOnPrice(product, parseInt(addonId as string)) * qty).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</span>
                                         </div>
                                     </div>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

                <!-- Footer Summary -->
                <div v-if="cart.length > 0" class="p-6 bg-white border-t rounded-t-3xl box-shadow sticky bottom-0 z-20">
                    <div class="flex flex-col gap-3 mb-6">
                        <div class="flex justify-between items-center text-gray-500 font-medium">
                            <span>Subtotal</span>
                            <span>{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</span>
                        </div>
                        <div class="flex justify-between items-center text-gray-800 font-bold text-xl">
                            <span>Total</span>
                            <span class="text-primary">{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</span>
                        </div>
                    </div>
                    
                    <button @click="currentPage = 2" class="w-full bg-zinc-900 text-white p-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-zinc-800 transition shadow-lg">
                        Próximo Passo
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Checkout Step -->
        <div v-else class="h-full font-inter p-6 flex flex-col gap-6 enter-content-animation">
            <button class="flex items-center gap-2 text-primary font-bold hover:gap-3 transition transition-all duration-200" @click="currentPage = 1">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m15 18-6-6 6-6"/></svg>
                Voltar
            </button>

            <div class="flex flex-col gap-6">
                <div>
                    <h2 class="text-2xl font-bold mb-1">Finalização</h2>
                    <p class="text-sm text-gray-500">Selecione o cliente e adicione observações se necessário.</p>
                </div>

                <div class="flex flex-col gap-2">
                    <div class="flex items-center justify-between">
                        <label class="font-bold text-sm uppercase tracking-wider text-gray-400">Selecionar Cliente</label>
                        <RouterLink :to="{name:'tables'}" class="text-xs text-primary font-bold hover:underline" @click="setIsOpen(false)">Nova Comanda</RouterLink>
                    </div>
                    <select v-model="selectedTabId" class="w-full p-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-primary/20 outline-none font-medium">
                        <option :value="null">Selecione uma comanda/mesa</option>
                        <option v-for="tab in flattenedTabs" :key="tab.id" :value="tab.id">
                            Mesa {{ tab.number }} - {{ tab.name || 'Anônimo' }}
                        </option>
                    </select>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="font-bold text-sm uppercase tracking-wider text-gray-400">Observações do Pedido</label>
                    <textarea
                        v-model="notes"
                        rows="4"
                        placeholder="Ex: Tirar cebola, ponto da carne mal passado..."
                        class="w-full p-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                    ></textarea>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="font-bold text-sm uppercase tracking-wider text-gray-400">Desconto (opcional)</label>
                    <input
                        v-model.number="discount"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="R$ 0,00"
                        class="w-full p-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                    />
                </div>

                <div class="mt-4 p-6 bg-zinc-900 rounded-3xl text-white shadow-xl">
                    <div class="flex justify-between items-center mb-6">
                        <div class="flex flex-col w-full">
                            <div class="flex justify-between mb-2">
                                <span class="text-xs opacity-50 uppercase font-bold tracking-widest">Subtotal</span>
                                <span class="text-sm opacity-50">{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                            </div>
                            <div v-if="discount > 0" class="flex justify-between mb-2">
                                <span class="text-xs opacity-50 uppercase font-bold tracking-widest">Desconto</span>
                                <span class="text-sm text-red-400">-{{ discount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-xs opacity-50 uppercase font-bold tracking-widest">Total</span>
                                <span class="text-3xl font-bold text-success">{{ (totalPrice - discount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                            </div>
                        </div>
                        <div class="h-12 w-12 bg-white/10 rounded-2xl flex items-center justify-center">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        </div>
                    </div>
                    
                    <button 
                        :disabled="!selectedTabId || isPlacingOrder" 
                        @click="placeOrder" 
                        class="w-full p-5 bg-primary text-white rounded-2xl font-bold hover:bg-opacity-90 transition disabled:opacity-20 flex items-center justify-center gap-3"
                    >
                        <span v-if="!isPlacingOrder">Confirmar Pedido</span>
                        <span v-else>Enviando...</span>
                        <svg v-if="!isPlacingOrder" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m12 5 7 7-7 7"/><path d="M5 12h14"/></svg>
                    </button>
                </div>
            </div>
        </div>
    </MyModal>
</template>

<script lang="ts" setup>
import { ref, watch, computed, onMounted } from 'vue'
import MyModal from '@/components/modal/MyModal.vue';
import { cart, commands, selectedCommand, apiBaseUrl, organizationSettings } from '@/global/state/GlobalState';
import { useToast } from "vue-toastification";
import axiosClient from '@/api/axios';

const props = defineProps<{ isOpen: boolean, setIsOpen: (v: boolean) => void }>()

const currentPage = ref<number>(1)
const notes = ref<string>('')
const discount = ref<number>(0)
const selectedTabId = ref<number | null>(null)
const isPlacingOrder = ref<boolean>(false)
const toast = useToast()

// Watchers
watch(() => cart.value, (newCart: any) => {
    localStorage.setItem('cart', JSON.stringify(newCart))
}, { deep: true })

// Initialize selected tab from global state
onMounted(() => {
    if (selectedCommand.value?.id) {
        selectedTabId.value = selectedCommand.value.id
    }
})

watch(() => selectedCommand.value, (newVal) => {
    if (newVal?.id) {
        selectedTabId.value = newVal.id
    }
})

// Computed
const flattenedTabs = computed(() => {
    const allTabs: any[] = []
    Object.values(commands.value).forEach((tabList: any) => {
        if (Array.isArray(tabList)) {
            allTabs.push(...tabList)
        }
    })
    return allTabs
})

const totalPrice = computed<number>(() => {
    return cart.value.reduce((acc: number, item: any) => acc + (item.unitPrice * item.quantity), 0)
})

// Item helper methods
function hasExtras(product: any) {
    const hasVariants = Object.values(product.selectedVariants || {}).some((ids: any) => ids.length > 0)
    const hasAddOns = Object.keys(product.selectedAddOns || {}).length > 0
    return hasVariants || hasAddOns
}

function getVariantGroupName(product: any, groupId: number) {
    return product.variantGroups?.find((g: any) => g.id === groupId)?.name || 'Opção'
}

function getVariantName(product: any, groupId: number, variantId: number) {
    const group = product.variantGroups?.find((g: any) => g.id === groupId)
    return group?.variants?.find((v: any) => v.id === variantId)?.name || ''
}

function getAddOnName(product: any, addonId: number) {
    return product.addOnGroups?.flatMap((g: any) => g.addOns).find((a: any) => a.id === addonId)?.name || ''
}

function getAddOnPrice(product: any, addonId: number) {
    return product.addOnGroups?.flatMap((g: any) => g.addOns).find((a: any) => a.id === addonId)?.price || 0
}

async function placeOrder() {
    if (!selectedTabId.value || cart.value.length === 0) return

    isPlacingOrder.value = true
    
    // Transform cart to the API format
    const productsPayload = cart.value.map((item: any) => {
        const productData: any = {
            id: item.id,
            quantity: item.quantity,
        }

        // Include item notes if present
        if (item.notes) {
            productData.notes = item.notes
        }

        // Flatten variants
        const variants: any[] = []
        Object.values(item.selectedVariants || {}).forEach((ids: any) => {
            ids.forEach((vId: number) => variants.push({ id: vId, quantity: item.quantity }))
        })
        if (variants.length > 0) productData.variants = variants

        // Flatten addons
        const addons: any[] = []
        Object.entries(item.selectedAddOns || {}).forEach(([id, qty]: [string, any]) => {
            addons.push({ id: parseInt(id), quantity: qty * item.quantity })
        })
        if (addons.length > 0) productData.addOns = addons

        return productData
    })

    const payload = {
        tabId: selectedTabId.value,
        notes: notes.value,
        discount: discount.value > 0 ? discount.value : undefined,
        type: 'waiter', // Waiter app usually creates waiter-type orders
        paymentMethod: 'cash', // Default or handled later? API needs one.
        products: productsPayload,
    }

    try {
        await axiosClient.post('/orders', payload)
        toast.success('Pedido confirmado com sucesso!')
        
        // Clean cart
        cart.value = []
        localStorage.removeItem('cart')
        notes.value = ''
        discount.value = 0
        currentPage.value = 1
        props.setIsOpen(false)
    } catch (error: any) {
        const message = error.response?.data?.errors?.[0]?.message || error.response?.data?.message || "Erro ao processar pedido"
        toast.error(message)
    } finally {
        isPlacingOrder.value = false
    }
}
</script>

<style scoped>
.box-shadow {
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.enter-content-animation {
    animation: slideIn 0.3s ease-out;
}
@keyframes slideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>